// Deterministic `/app` resolution (Phase 1 / T1.6A, Final Design v1.1 §11.2).
//
// Pure: it takes the discovery response the server just returned plus whatever
// the browser remembered, and answers where to go. No I/O, no storage access,
// no React — so the whole matrix is testable, and there is exactly one place
// the order lives.
//
// The order is locked, and each step exists because of a way the previous one
// can be wrong:
//   A. the remembered pair, but ONLY if it still matches an ACTIVE membership
//      and a Location that membership may reach;
//   B. otherwise a deterministic authorized ACTIVE business and a Location it
//      actually offers — its primary when that is in scope, else the first the
//      server said this member may reach;
//   C. otherwise an owned PROVISIONAL business, which resumes onboarding and
//      never becomes the acting tenant;
//   D. otherwise onboarding.
//
// Nothing here creates anything, and provisional never wins over active.
import type { ActiveTenant } from './active-tenant';

export interface DiscoveredLocationLike {
  id: string;
  name: string;
  primary: boolean;
}

export interface DiscoveredBusinessLike {
  id: string;
  name: string;
  slug: string;
  lifecycleStatus: 'provisional' | 'active';
  role: string;
  scoped: boolean;
  locationScope: string[] | null;
  /** Already narrowed server-side to what this member may reach. */
  locations: DiscoveredLocationLike[];
}

export interface DiscoveryResponse {
  businesses: DiscoveredBusinessLike[];
  counts?: { active: number; provisional: number };
}

export type AppResolution =
  /** Open this Location. `remembered` is false when the pair was newly chosen. */
  | { kind: 'location'; businessId: string; locationId: string; remembered: boolean }
  /** An owned provisional business: resume onboarding, do not act as it. */
  | { kind: 'resume'; businessId: string }
  /** Canonical onboarding. */
  | { kind: 'start' }
  /**
   * An ACTIVE business the caller may use, with no Location they may open.
   * Explicit rather than guessed: sending them into a Location the server did
   * not offer would be inventing one, and falling through to onboarding would
   * tell an existing customer they have no business.
   */
  | { kind: 'no-location'; businessId: string };

const activeOnly = (r: DiscoveryResponse) => r.businesses.filter((b) => b.lifecycleStatus === 'active');

/** Deterministic: the server sorts by id, so this is stable across reloads. */
function firstActive(r: DiscoveryResponse): DiscoveredBusinessLike | undefined {
  return activeOnly(r)[0];
}

/** Primary if the member may reach it, else the first they may reach. */
function preferredLocation(b: DiscoveredBusinessLike): DiscoveredLocationLike | undefined {
  return b.locations.find((l) => l.primary) ?? b.locations[0];
}

export function resolveApp(discovery: DiscoveryResponse, remembered: ActiveTenant | null): AppResolution {
  // A. the remembered pair — matched against what the server just authorized.
  if (remembered) {
    const biz = activeOnly(discovery).find((x) => x.id === remembered.businessId);
    // `biz.locations` is already scope-narrowed, so this single check covers
    // "the location belongs to that business" AND "the membership may reach
    // it". A scoped member whose stored pair points at a sibling falls through
    // to B rather than being redirected there.
    if (biz && biz.locations.some((l) => l.id === remembered.locationId)) {
      return { kind: 'location', businessId: biz.id, locationId: remembered.locationId, remembered: true };
    }
  }

  // B. a deterministic authorized ACTIVE business.
  const active = firstActive(discovery);
  if (active) {
    const loc = preferredLocation(active);
    if (loc) return { kind: 'location', businessId: active.id, locationId: loc.id, remembered: false };
    return { kind: 'no-location', businessId: active.id };
  }

  // C. an owned provisional business resumes onboarding; it is never acting.
  const provisional = discovery.businesses.find((x) => x.lifecycleStatus === 'provisional');
  if (provisional) return { kind: 'resume', businessId: provisional.id };

  // D. nothing to act as.
  return { kind: 'start' };
}
